/**
 * Sovereign Ash: Nareth Protocol
 * Test Arena Scene
 *
 * A walkable test environment demonstrating:
 * - WebGPU/WebGL2 rendering via Babylon.js
 * - Fixed-step simulation loop
 * - Basic walker movement with mass/inertia
 * - Placeholder geometry (NOT final assets)
 */

import {
  Engine,
  Scene,
  ArcRotateCamera,
  HemisphericLight,
  DirectionalLight,
  Vector3,
  Color3,
  Color4,
  MeshBuilder,
  StandardMaterial,
  ShadowGenerator,
  Mesh,
  TransformNode,
} from '@babylonjs/core';
import '@babylonjs/loaders';

import {
  SimulationLoop,
  lerp,
  lerpAngle,
  type SimEntity,
} from '@sovereign-ash/game-core';
import type { RendererCapability } from '@sovereign-ash/rendering';

// ============================================================================
// Walker Physics Constants (placeholder values)
// ============================================================================

const MAX_SPEED = 12; // m/s
const ACCELERATION = 4; // m/s²
const DECELERATION = 6; // m/s²
const TURN_RATE = 45; // deg/s
const TORSO_TRAVERSE = 60; // deg/s

// ============================================================================
// Input State
// ============================================================================

interface InputState {
  forward: boolean;
  backward: boolean;
  left: boolean;
  right: boolean;
  turnLeft: boolean;
  turnRight: boolean;
  torsoLeft: boolean;
  torsoRight: boolean;
}

// ============================================================================
// Walker Entity
// ============================================================================

class WalkerEntity implements SimEntity {
  id = 'player-walker';

  // Position and rotation
  x = 0;
  z = 0;
  legHeading = 0; // degrees
  torsoHeading = 0; // degrees (relative to legs)

  // Velocity
  speed = 0;
  targetSpeed = 0;

  // Interpolation state
  prevX = 0;
  prevZ = 0;
  prevLegHeading = 0;
  prevTorsoHeading = 0;

  // Input
  input: InputState = {
    forward: false,
    backward: false,
    left: false,
    right: false,
    turnLeft: false,
    turnRight: false,
    torsoLeft: false,
    torsoRight: false,
  };

  update(dt: number, _tick: number): void {
    // Store previous state for interpolation
    this.prevX = this.x;
    this.prevZ = this.z;
    this.prevLegHeading = this.legHeading;
    this.prevTorsoHeading = this.torsoHeading;

    // Calculate target speed from input
    if (this.input.forward) {
      this.targetSpeed = MAX_SPEED;
    } else if (this.input.backward) {
      this.targetSpeed = -MAX_SPEED * 0.5; // Reverse is slower
    } else {
      this.targetSpeed = 0;
    }

    // Apply acceleration/deceleration (mass-dependent)
    const accelRate = this.targetSpeed > this.speed ? ACCELERATION : DECELERATION;
    const speedDiff = this.targetSpeed - this.speed;
    const maxChange = accelRate * dt;

    if (Math.abs(speedDiff) <= maxChange) {
      this.speed = this.targetSpeed;
    } else {
      this.speed += Math.sign(speedDiff) * maxChange;
    }

    // Turning (mass-dependent rate)
    if (this.input.turnLeft) {
      this.legHeading += TURN_RATE * dt;
    }
    if (this.input.turnRight) {
      this.legHeading -= TURN_RATE * dt;
    }

    // Torso traverse (independent of legs)
    if (this.input.torsoLeft) {
      this.torsoHeading += TORSO_TRAVERSE * dt;
    }
    if (this.input.torsoRight) {
      this.torsoHeading -= TORSO_TRAVERSE * dt;
    }

    // Clamp torso heading to ±120 degrees
    this.torsoHeading = Math.max(-120, Math.min(120, this.torsoHeading));

    // Update position based on heading and speed
    const headingRad = (this.legHeading * Math.PI) / 180;
    this.x += Math.sin(headingRad) * this.speed * dt;
    this.z += Math.cos(headingRad) * this.speed * dt;

    // Arena bounds
    const ARENA_SIZE = 90;
    this.x = Math.max(-ARENA_SIZE, Math.min(ARENA_SIZE, this.x));
    this.z = Math.max(-ARENA_SIZE, Math.min(ARENA_SIZE, this.z));
  }

  /**
   * Get interpolated position for rendering.
   */
  getInterpolated(alpha: number): {
    x: number;
    z: number;
    legHeading: number;
    torsoHeading: number;
  } {
    return {
      x: lerp(this.prevX, this.x, alpha),
      z: lerp(this.prevZ, this.z, alpha),
      legHeading: lerpAngle(this.prevLegHeading, this.legHeading, alpha),
      torsoHeading: lerpAngle(this.prevTorsoHeading, this.torsoHeading, alpha),
    };
  }
}

// ============================================================================
// Test Arena
// ============================================================================

export interface ArenaMetrics {
  fps: number;
  tick: number;
  simTime: number;
  entityCount: number;
  position: string;
}

export class TestArena {
  private canvas: HTMLCanvasElement;
  private capabilities: RendererCapability;
  private engine: Engine | WebGPUEngine | null = null;
  private scene: Scene | null = null;
  private simLoop: SimulationLoop;
  private walker: WalkerEntity;

  // Meshes
  private walkerRoot: TransformNode | null = null;
  private walkerTorso: Mesh | null = null;
  private walkerHead: Mesh | null = null;

  constructor(canvas: HTMLCanvasElement, capabilities: RendererCapability) {
    this.canvas = canvas;
    this.capabilities = capabilities;
    this.simLoop = new SimulationLoop(12345); // Fixed seed for determinism
    this.walker = new WalkerEntity();
  }

  /**
   * Initialize the Babylon.js scene.
   */
  async initialize(): Promise<void> {
    console.log('[Arena] Initializing with backend:', this.capabilities.backend);
    
    // Ensure canvas has dimensions
    this.canvas.width = this.canvas.clientWidth || window.innerWidth;
    this.canvas.height = this.canvas.clientHeight || window.innerHeight;
    
    // Create engine - prefer WebGL2 for broader compatibility
    // WebGPU can be enabled later when support is more stable
    try {
      this.engine = new Engine(this.canvas, true, {
        powerPreference: 'high-performance',
        antialias: true,
        stencil: true,
      });
      console.log('[Arena] WebGL2 engine initialized');
    } catch (error) {
      console.error('[Arena] Failed to create engine:', error);
      throw new Error('Failed to initialize WebGL2 renderer');
    }

    // Create scene
    this.scene = new Scene(this.engine);
    this.scene.clearColor = new Color4(0.04, 0.04, 0.06, 1);

    // Setup camera
    this.setupCamera();

    // Setup lighting
    this.setupLighting();

    // Create arena environment
    this.createArena();

    // Create placeholder walker
    this.createWalker();

    // Register walker with simulation
    this.simLoop.addEntity(this.walker);

    // Setup input handling
    this.setupInput();

    // Setup render loop
    this.simLoop.onRender((alpha: number) => {
      this.updateVisuals(alpha);
      this.scene?.render();
    });

    // Handle resize
    window.addEventListener('resize', () => {
      this.engine?.resize();
    });

    console.log('[Arena] Scene initialized');
  }

  /**
   * Setup the camera.
   */
  private setupCamera(): void {
    if (!this.scene) return;

    const camera = new ArcRotateCamera(
      'camera',
      -Math.PI / 2,
      Math.PI / 3,
      30,
      new Vector3(0, 5, 0),
      this.scene
    );
    camera.attachControl(this.canvas, true);
    camera.lowerRadiusLimit = 10;
    camera.upperRadiusLimit = 100;
    camera.lowerBetaLimit = 0.1;
    camera.upperBetaLimit = Math.PI / 2 - 0.1;
    camera.wheelDeltaPercentage = 0.01;
    camera.panningSensibility = 50;
  }

  /**
   * Setup lighting.
   */
  private setupLighting(): void {
    if (!this.scene) return;

    // Ambient light
    const hemiLight = new HemisphericLight('hemi', new Vector3(0, 1, 0), this.scene);
    hemiLight.intensity = 0.4;
    hemiLight.diffuse = new Color3(0.9, 0.9, 1.0);
    hemiLight.groundColor = new Color3(0.2, 0.2, 0.3);

    // Directional sun light
    const sunLight = new DirectionalLight('sun', new Vector3(-0.5, -1, -0.5), this.scene);
    sunLight.position = new Vector3(50, 100, 50);
    sunLight.intensity = 0.8;
    sunLight.diffuse = new Color3(1.0, 0.95, 0.9);

    // Shadow generator
    const shadowGen = new ShadowGenerator(2048, sunLight);
    shadowGen.useBlurExponentialShadowMap = true;
    shadowGen.blurKernel = 32;

    // Store for later use
    (this.scene as Scene & { shadowGen: ShadowGenerator }).shadowGen = shadowGen;
  }

  /**
   * Create the arena environment.
   */
  private createArena(): void {
    if (!this.scene) return;

    // Ground plane
    const ground = MeshBuilder.CreateGround('ground', {
      width: 200,
      height: 200,
      subdivisions: 4,
    }, this.scene);

    const groundMat = new StandardMaterial('groundMat', this.scene);
    groundMat.diffuseColor = new Color3(0.15, 0.15, 0.18);
    groundMat.specularColor = new Color3(0.1, 0.1, 0.1);
    ground.material = groundMat;
    ground.receiveShadows = true;

    // Grid helper
    const gridSize = 200;
    const gridDivisions = 20;
    const gridLines: Vector3[][] = [];

    for (let i = -gridDivisions / 2; i <= gridDivisions / 2; i++) {
      const pos = (i / gridDivisions) * gridSize;
      gridLines.push([
        new Vector3(pos, 0.01, -gridSize / 2),
        new Vector3(pos, 0.01, gridSize / 2),
      ]);
      gridLines.push([
        new Vector3(-gridSize / 2, 0.01, pos),
        new Vector3(gridSize / 2, 0.01, pos),
      ]);
    }

    const grid = MeshBuilder.CreateLineSystem('grid', { lines: gridLines }, this.scene);
    grid.color = new Color3(0.3, 0.3, 0.4);
    grid.alpha = 0.3;

    // Boundary markers
    this.createBoundaryMarkers();

    // Some obstacle boxes for reference
    this.createObstacles();
  }

  /**
   * Create boundary markers.
   */
  private createBoundaryMarkers(): void {
    if (!this.scene) return;

    const markerMat = new StandardMaterial('markerMat', this.scene);
    markerMat.diffuseColor = new Color3(0.96, 0.65, 0.14); // Amber
    markerMat.emissiveColor = new Color3(0.48, 0.32, 0.07);

    const positions = [
      [-90, 0, -90], [90, 0, -90], [-90, 0, 90], [90, 0, 90],
    ];

    for (const [x, , z] of positions) {
      const marker = MeshBuilder.CreateCylinder('marker', {
        height: 8,
        diameter: 1,
        tessellation: 8,
      }, this.scene);
      marker.position = new Vector3(x, 4, z);
      marker.material = markerMat;
    }
  }

  /**
   * Create obstacle boxes.
   */
  private createObstacles(): void {
    if (!this.scene) return;

    const obstacleMat = new StandardMaterial('obstacleMat', this.scene);
    obstacleMat.diffuseColor = new Color3(0.25, 0.25, 0.3);
    obstacleMat.specularColor = new Color3(0.2, 0.2, 0.2);

    const obstacles = [
      { pos: [20, 2.5, 20], size: [5, 5, 5] },
      { pos: [-30, 4, 10], size: [8, 8, 8] },
      { pos: [10, 1.5, -40], size: [10, 3, 4] },
      { pos: [-20, 3, -25], size: [6, 6, 6] },
    ];

    const shadowGen = (this.scene as Scene & { shadowGen?: ShadowGenerator }).shadowGen;

    for (const { pos, size } of obstacles) {
      const box = MeshBuilder.CreateBox('obstacle', {
        width: size[0],
        height: size[1],
        depth: size[2],
      }, this.scene);
      box.position = new Vector3(pos[0], pos[1], pos[2]);
      box.material = obstacleMat;
      box.receiveShadows = true;
      if (shadowGen) {
        shadowGen.addShadowCaster(box);
      }
    }
  }

  /**
   * Create placeholder walker geometry.
   * NOTE: This is NOT final art. Placeholder boxes only.
   */
  private createWalker(): void {
    if (!this.scene) return;

    // Root transform
    this.walkerRoot = new TransformNode('walkerRoot', this.scene);

    // Materials
    const legMat = new StandardMaterial('legMat', this.scene);
    legMat.diffuseColor = new Color3(0.2, 0.2, 0.22); // Dark graphite

    const torsoMat = new StandardMaterial('torsoMat', this.scene);
    torsoMat.diffuseColor = new Color3(0.85, 0.83, 0.78); // Pale ceramic

    const headMat = new StandardMaterial('headMat', this.scene);
    headMat.diffuseColor = new Color3(0.3, 0.3, 0.35);
    headMat.emissiveColor = new Color3(0.1, 0.4, 0.35); // Cyan sensor glow

    // Legs (placeholder: two boxes)
    const leftLeg = MeshBuilder.CreateBox('leftLeg', {
      width: 1.2,
      height: 6,
      depth: 1.5,
    }, this.scene);
    leftLeg.position = new Vector3(-1.5, 3, 0);
    leftLeg.material = legMat;
    leftLeg.parent = this.walkerRoot;

    const rightLeg = MeshBuilder.CreateBox('rightLeg', {
      width: 1.2,
      height: 6,
      depth: 1.5,
    }, this.scene);
    rightLeg.position = new Vector3(1.5, 3, 0);
    rightLeg.material = legMat;
    rightLeg.parent = this.walkerRoot;

    // Pelvis
    const pelvis = MeshBuilder.CreateBox('pelvis', {
      width: 4,
      height: 1.5,
      depth: 3,
    }, this.scene);
    pelvis.position = new Vector3(0, 6.5, 0);
    pelvis.material = legMat;
    pelvis.parent = this.walkerRoot;

    // Torso (rotates independently)
    this.walkerTorso = MeshBuilder.CreateBox('torso', {
      width: 5,
      height: 4,
      depth: 4,
    }, this.scene);
    this.walkerTorso.position = new Vector3(0, 9.5, 0);
    this.walkerTorso.material = torsoMat;
    this.walkerTorso.parent = this.walkerRoot;

    // Head/sensor
    this.walkerHead = MeshBuilder.CreateBox('head', {
      width: 2,
      height: 1.5,
      depth: 2,
    }, this.scene);
    this.walkerHead.position = new Vector3(0, 12, 0.5);
    this.walkerHead.material = headMat;
    this.walkerHead.parent = this.walkerTorso;

    // Weapon placeholder (right arm)
    const weapon = MeshBuilder.CreateCylinder('weapon', {
      height: 4,
      diameter: 0.6,
      tessellation: 8,
    }, this.scene);
    weapon.rotation.x = Math.PI / 2;
    weapon.position = new Vector3(3, 9.5, 1.5);
    weapon.material = legMat;
    weapon.parent = this.walkerTorso;

    // Add to shadow generator
    const shadowGen = (this.scene as Scene & { shadowGen?: ShadowGenerator }).shadowGen;
    if (shadowGen) {
      shadowGen.addShadowCaster(leftLeg);
      shadowGen.addShadowCaster(rightLeg);
      shadowGen.addShadowCaster(pelvis);
      shadowGen.addShadowCaster(this.walkerTorso);
      shadowGen.addShadowCaster(this.walkerHead);
      shadowGen.addShadowCaster(weapon);
    }

    console.log('[Arena] Placeholder walker created (NOT final art)');
  }

  /**
   * Setup keyboard input.
   */
  private setupInput(): void {
    const keyMap: Record<string, keyof InputState> = {
      'KeyW': 'forward',
      'ArrowUp': 'forward',
      'KeyS': 'backward',
      'ArrowDown': 'backward',
      'KeyA': 'turnLeft',
      'ArrowLeft': 'turnLeft',
      'KeyD': 'turnRight',
      'ArrowRight': 'turnRight',
      'KeyQ': 'torsoLeft',
      'KeyE': 'torsoRight',
    };

    window.addEventListener('keydown', (e) => {
      const action = keyMap[e.code];
      if (action) {
        e.preventDefault();
        this.walker.input[action] = true;
      }
    });

    window.addEventListener('keyup', (e) => {
      const action = keyMap[e.code];
      if (action) {
        e.preventDefault();
        this.walker.input[action] = false;
      }
    });

    // Prevent context menu on right click
    this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  /**
   * Update visual positions from simulation state.
   */
  private updateVisuals(alpha: number): void {
    if (!this.walkerRoot || !this.walkerTorso) return;

    const state = this.walker.getInterpolated(alpha);

    // Update root position and leg heading
    this.walkerRoot.position.x = state.x;
    this.walkerRoot.position.z = state.z;
    this.walkerRoot.rotation.y = (state.legHeading * Math.PI) / 180;

    // Update torso rotation (relative to legs)
    this.walkerTorso.rotation.y = (state.torsoHeading * Math.PI) / 180;

    // Update camera target to follow walker
    const camera = this.scene?.activeCamera as ArcRotateCamera;
    if (camera) {
      camera.target.x = lerp(camera.target.x, state.x, 0.1);
      camera.target.z = lerp(camera.target.z, state.z, 0.1);
      camera.target.y = 5;
    }
  }

  /**
   * Start the simulation and render loop.
   */
  start(): void {
    this.simLoop.start();
    console.log('[Arena] Simulation started');
  }

  /**
   * Stop the simulation.
   */
  stop(): void {
    this.simLoop.stop();
  }

  /**
   * Get performance metrics.
   */
  getMetrics(): ArenaMetrics {
    const simState = this.simLoop.getState();
    const engineFps = this.engine?.getFps() ?? 0;

    return {
      fps: engineFps,
      tick: simState.tick,
      simTime: simState.time,
      entityCount: this.simLoop.getMetrics().entityCount,
      position: `(${this.walker.x.toFixed(1)}, ${this.walker.z.toFixed(1)})`,
    };
  }

  /**
   * Dispose of all resources.
   */
  dispose(): void {
    this.simLoop.stop();
    this.scene?.dispose();
    this.engine?.dispose();
  }
}
